/**
 * URL Shortener Service
 *
 * Client for the Meetball URL shortener worker (go.meetball.fun).
 * Provides short URLs for sharing with graceful fallback to full URLs.
 */

import { logger } from '@utils/logger';

// URL shortener worker endpoint
const SHORTENER_BASE_URL: string =
  (import.meta.env.VITE_URL_SHORTENER_URL as string | undefined) || 'https://go.meetball.fun';

// Timeout for shortener requests (ms)
const SHORTENER_TIMEOUT = 3000;

/**
 * Metadata to store with short URLs for analytics
 */
export interface ShortenMetadata {
  entityType?: 'event' | 'question' | 'profile';
  entityId?: string;
  sharerId?: string;
}

/**
 * Response from the shortener API
 */
interface ShortenResponse {
  code: string;
  shortUrl: string;
}

/**
 * Shorten a URL using the Meetball URL shortener service
 *
 * @param url - The full URL to shorten
 * @param metadata - Optional metadata for analytics
 * @returns The short URL, or the original URL if shortening fails
 */
export async function shortenUrl(url: string, metadata?: ShortenMetadata): Promise<string> {
  // Skip shortening in development unless explicitly enabled
  if (import.meta.env.DEV && !import.meta.env.VITE_ENABLE_URL_SHORTENER) {
    logger.debug('URL shortener disabled in development, using full URL');
    return url;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, SHORTENER_TIMEOUT);

    const response = await fetch(`${SHORTENER_BASE_URL}/shorten`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url, metadata }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Shortener returned ${response.status}`);
    }

    const data = (await response.json()) as ShortenResponse;

    if (!data.shortUrl) {
      throw new Error('Invalid response from shortener');
    }

    logger.debug(`Shortened URL: ${url} -> ${data.shortUrl}`);
    return data.shortUrl;
  } catch (error) {
    // Graceful fallback - log warning and return original URL
    if (error instanceof Error && error.name === 'AbortError') {
      logger.warn('URL shortener request timed out, using full URL');
    } else {
      logger.warn('URL shortener failed, using full URL:', error);
    }
    return url;
  }
}

/**
 * Check if the URL shortener service is available
 */
export async function isShortenerAvailable(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 2000);

    const response = await fetch(`${SHORTENER_BASE_URL}/health`, {
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    return response.ok;
  } catch {
    return false;
  }
}
